import { useMemo } from 'react';
import { getGamesByCategory, getLatestGames, type Game } from '@lib/content';
import { GAME_LIMITS } from '../config';

export interface GameRecommendations {
  sidebarGames: Game[];
  moreGames: Game[];
}

/**
 * Get series/version games for current game
 */
function getSeriesGames(currentGame: Game, allCategoryGames: Game[]): Game[] {
  const currentTitle = currentGame.title.toLowerCase();
  const currentSlug = currentGame.slug.toLowerCase();

  // Extract base name from title (remove numbers and common words)
  const getBaseName = (title: string) => {
    return title
      .toLowerCase()
      .replace(/\b(1|2|3|4|5|6|7|8|9|10|i+|ii+|iii+|iv+|v+)\b/g, '')
      .replace(/\b(epic|battle|new|super|mega|ultra|pro|plus|deluxe|winter|spooky|pool|party)\b/g, '')
      .replace(/[-:\s]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  };

  const getBaseSlug = (slug: string) => {
    return slug
      .replace(/-(\d+)$/, '') // Remove trailing numbers
      .replace(/-(epic|battle|new|super|mega|ultra|pro|plus|deluxe|winter|spooky|pool|party)(-\d+)?$/, '')
      .replace(/-(1|2|3|4|5|6|7|8|9|10)(-\w+)*$/, '');
  };

  const currentBaseName = getBaseName(currentGame.title);
  const currentBaseSlug = getBaseSlug(currentGame.slug);

  // Find series games
  const seriesGames = allCategoryGames.filter(game => {
    if (game.slug === currentGame.slug) return false;

    const gameBaseName = getBaseName(game.title);
    const gameBaseSlug = getBaseSlug(game.slug);

    // Check if same series by title or slug
    return (currentBaseName === gameBaseName && currentBaseName.length > 3) ||
      (currentBaseSlug === gameBaseSlug && currentBaseSlug.length > 3) ||
      // Manual series mappings for common cases
      isInSameSeries(currentGame.slug, game.slug);
  });

  // Sort by version/number (try to get sequential order)
  return seriesGames.sort((a, b) => {
    const aNum = extractNumber(a.title) || extractNumber(a.slug) || 0;
    const bNum = extractNumber(b.title) || extractNumber(b.slug) || 0;
    return aNum - bNum;
  });
}

/**
 * Extract number from title/slug for version ordering
 */
function extractNumber(text: string): number | null {
  const match = text.match(/\b(\d+)\b/);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Manual series detection for common game series (based on actual games in project)
 */
function isInSameSeries(slug1: string, slug2: string): boolean {
  const seriesMap: Record<string, string[]> = {

    // Shooter Series
    'alien-shooter': ['fps-assault-shooter', 'pixel-shooter', 'rooftop-shooters', 'funny-shooter', 'funny-shooter-2', 'funny-shooter-3d'],
    'fps-assault-shooter': ['alien-shooter', 'pixel-shooter', 'rooftop-shooters'],
    'pixel-shooter': ['alien-shooter', 'fps-assault-shooter', 'rooftop-shooters'],
    'rooftop-shooters': ['pixel-shooter', 'fps-assault-shooter', 'rooftop-snipers'],
    'rooftop-snipers': ['rooftop-snipers-2', 'rooftop-shooters'],
    'rooftop-snipers-2': ['rooftop-snipers', 'rooftop-shooters'],
    'funny-shooter': ['funny-shooter-2', 'funny-shooter-3d', 'funny-shooter-destroy-all'],
    'funny-shooter-2': ['funny-shooter', 'funny-shooter-3d'],
    'funny-shooter-3d': ['funny-shooter', 'funny-shooter-2'],
    'fnaf-shooter': ['fps-assault-shooter', 'alien-shooter'],

    // Horror Series
    'backrooms': ['backrooms-2d', 'the-backrooms', 'the-backrooms-3d'],
    'backrooms-2d': ['backrooms', 'the-backrooms'],
    'the-backrooms': ['backrooms', 'backrooms-2d'],
    'the-backrooms-3d': ['backrooms', 'the-backrooms'],
    'granny': ['granny-2', 'granny-3'],
    'granny-2': ['granny', 'granny-3'],
    'granny-3': ['granny', 'granny-2'],
    'slender': ['slender-multiplayer'],
    'slender-multiplayer': ['slender'],
    'evil-nun-schools-out': ['granny', 'slender'],
    'scary-maze': ['slender', 'evil-nun-schools-out'],
    'thats-not-my-neighbor': ['granny', 'slender'],

    // FNAF Series (quan trọng SEO nhất)
    'five-nights-at-freddys-2': ['five-nights-at-freddys-3', 'five-nights-at-freddys-4', 'fnaf-sister-location'],
    'five-nights-at-freddys-3': ['five-nights-at-freddys-2', 'five-nights-at-freddys-4'],
    'five-nights-at-freddys-4': ['five-nights-at-freddys-2', 'five-nights-at-freddys-3'],
    'fnaf-sister-location': ['five-nights-at-freddys-2', 'fnaf-ultimate-custom-night'],
    'fnaf-ultimate-custom-night': ['fnaf-sister-location', 'fnaf-world'],
    'fnaf-world': ['fnaf-sister-location', 'fnaf-ultimate-custom-night'],
    'five-nights-at-candys': ['five-nights-at-candys-2', 'five-nights-at-candys-3'],
    'five-nights-at-candys-2': ['five-nights-at-candys', 'five-nights-at-candys-3'],
    'five-nights-at-candys-3': ['five-nights-at-candys', 'five-nights-at-candys-2'],

    // Racing Series
    'death-chase': ['death-chase-2', 'death-chase-3'],
    'death-chase-2': ['death-chase', 'death-chase-3'],
    'death-chase-3': ['death-chase', 'death-chase-2'],
    'traffic-rider': ['top-speed-racing-3d', 'furious-racing-3d'],
    'top-speed-racing-3d': ['traffic-rider', 'furious-racing-3d'],
    'furious-racing-3d': ['traffic-rider', 'top-speed-racing-3d'],

    // Platform / Runner Series
    'geometry-dash': ['slope', 'electron-dash', 'level-devil'],
    'slope': ['geometry-dash', 'electron-dash'],
    'electron-dash': ['geometry-dash', 'slope'],
    'level-devil': ['geometry-dash', 'slope'],
    'subway-surfers': ['subway-runner'],
    'subway-runner': ['subway-surfers'],
    'vex-7': ['geometry-dash', 'slope'],
    'dreadhead-parkour': ['vex-7', 'geometry-dash'],

    // Stickman / Prison Series
    'escaping-the-prison': ['fleeing-the-complex'],
    'fleeing-the-complex': ['escaping-the-prison'],

    // Simulator Series
    'dog-simulator-3d': ['dragon-simulator-3d', 'fox-simulator-3d'],
    'dragon-simulator-3d': ['dog-simulator-3d', 'fox-simulator-3d'],
    'fox-simulator-3d': ['dog-simulator-3d', 'dragon-simulator-3d'],

    // IO / Multiplayer Series
    'deadshot-io': ['paper-io-2', 'bigmonsterzio'],
    'paper-io-2': ['deadshot-io', 'bigmonsterzio'],
    'bigmonsterzio': ['deadshot-io', 'paper-io-2'],
    'among-us': ['bigmonsterzio', 'paper-io-2'],

    // Zombie Series
    'plants-vs-zombies': ['zombie-shooter-2d', 'zombie-killer'],
    'zombie-shooter-2d': ['plants-vs-zombies', 'zombie-killer'],
    'zombie-killer': ['plants-vs-zombies', 'zombie-shooter-2d'],

    // FNF Series
    'friday-night-funkin': ['fnf-vs-shaggy', 'fnf-vs-garcello', 'fnf-minus'],
    'fnf-vs-shaggy': ['friday-night-funkin', 'fnf-vs-garcello'],
    'fnf-vs-garcello': ['friday-night-funkin', 'fnf-vs-shaggy'],

    // Adventure / Story
    'deltarune-1': ['deltarune-2', 'deltarune-3', 'deltarune-4'],
    'deltarune-2': ['deltarune-1', 'deltarune-3'],
    'deltarune-3': ['deltarune-1', 'deltarune-2'],
    'deltarune-4': ['deltarune-1', 'deltarune-2'],

    'undertale-yellow': ['deltarune-1', 'deltarune-2'],
    'omori-web-port': ['undertale-yellow', 'deltarune-1'],

    // Puzzle / Casual
    'bubble-shooter': ['panda-bubble-shooter', 'arkadiumx27s-bubble-shooter'],
    'panda-bubble-shooter': ['bubble-shooter', 'arkadiumx27s-bubble-shooter'],

    // Funny / Physics
    'elastic-man': ['make-it-meme', 'life-the-game'],
    'make-it-meme': ['elastic-man', 'life-the-game'],

  };

  return seriesMap[slug1]?.includes(slug2) ?? false;
}

/**
 * Enhanced game recommendations with series/version priority
 * Priority: Featured games > Series games > Same category games > Latest games
 */
export function useGameRecommendations(game: Game | null): GameRecommendations {
  return useMemo(() => {
    if (!game) return { sidebarGames: [], moreGames: [] };

    // Define priority games for homepage sidebar based on new categories
    const priorityGames = [
      'five-nights-at-candys',
      'five-nights-at-candys-2',
      'five-nights-at-candys-3',
      'five-nights-at-freddys-2',
      'five-nights-at-freddys-3',
      'five-nights-at-freddys-4',
      'five-nights-at-osaka-s',
      'five-nights-at-winstons',
      'fnaf-five-nights-at-freddys-2',
      'fnaf-five-nights-at-freddys-3',
      'fnaf-five-nights-at-freddys-4',
      'fnaf-pizzeria-simulator',
      'fnaf-sister-location',
      'fnaf-ultimate-custom-night',
      'fnaf-world'
    ];

    // Get all games from all categories of current game
    const allCategories = game.categories || [];
    const allRelatedGames: Game[] = [];
    const usedSlugs = new Set<string>([game.slug]);

    // Collect games from all categories (not just primary)
    for (const category of allCategories) {
      const categoryGames = getGamesByCategory(category).filter((g) => !usedSlugs.has(g.slug));
      allRelatedGames.push(...categoryGames);
      categoryGames.forEach(g => usedSlugs.add(g.slug));
    }

    // PRIORITY 1: Get series/version games first
    const seriesGames = getSeriesGames(game, allRelatedGames);

    // PRIORITY 2: Get priority games from the current game's categories
    const priorityRelatedGames = allRelatedGames.filter(g =>
      priorityGames.includes(g.slug) && !seriesGames.some(sg => sg.slug === g.slug)
    ).sort((a, b) => {
      const aIndex = priorityGames.indexOf(a.slug);
      const bIndex = priorityGames.indexOf(b.slug);
      return aIndex - bIndex;
    });

    // PRIORITY 3: Get remaining category games (excluding series and priority games)
    const remainingCategoryGames = allRelatedGames.filter(g =>
      !seriesGames.some(sg => sg.slug === g.slug) &&
      !priorityRelatedGames.some(pg => pg.slug === g.slug)
    );

    // PRIORITY 4: Get latest games as fallback
    const latestGames = getLatestGames(GAME_LIMITS.LATEST_GAMES_POOL)
      .filter((g) => g.slug !== game.slug && !usedSlugs.has(g.slug));

    // Build sidebar with priority order
    const sidebar: Game[] = [];
    const finalUsedSlugs = new Set<string>([game.slug]);

    // Add series games first (highest priority for related games)
    for (const seriesGame of seriesGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(seriesGame.slug)) {
        sidebar.push(seriesGame);
        finalUsedSlugs.add(seriesGame.slug);
      }
    }

    // Add priority games from same categories
    for (const priorityGame of priorityRelatedGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(priorityGame.slug)) {
        sidebar.push(priorityGame);
        finalUsedSlugs.add(priorityGame.slug);
      }
    }

    // Add remaining category games
    for (const categoryGame of remainingCategoryGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(categoryGame.slug)) {
        sidebar.push(categoryGame);
        finalUsedSlugs.add(categoryGame.slug);
      }
    }

    // Fill up with latest games if needed
    for (const latestGame of latestGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(latestGame.slug)) {
        sidebar.push(latestGame);
        finalUsedSlugs.add(latestGame.slug);
      }
    }

    // Build more games section
    const more: Game[] = [];

    // Add remaining series games that didn't fit in sidebar
    for (const seriesGame of seriesGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(seriesGame.slug)) {
        more.push(seriesGame);
        finalUsedSlugs.add(seriesGame.slug);
      }
    }

    // Add remaining priority games
    for (const priorityGame of priorityRelatedGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(priorityGame.slug)) {
        more.push(priorityGame);
        finalUsedSlugs.add(priorityGame.slug);
      }
    }

    // Add remaining category games
    for (const categoryGame of remainingCategoryGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(categoryGame.slug)) {
        more.push(categoryGame);
        finalUsedSlugs.add(categoryGame.slug);
      }
    }

    // Fill up with latest games if needed
    for (const latestGame of latestGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(latestGame.slug)) {
        more.push(latestGame);
        finalUsedSlugs.add(latestGame.slug);
      }
    }

    return { sidebarGames: sidebar, moreGames: more };
  }, [game]);
}
